import { supabase } from "@/integrations/supabase/client";

/**
 * Phase 4 guardrail: fetch the set of control codes that have at least one
 * pre-filled remediation template for the given questionnaire. The Findings
 * dropdown should ONLY surface controls in this set so reviewers never select
 * a control that would produce an empty finding.
 *
 * Cached per questionnaire for the lifetime of the page.
 *
 * Use clearValidTemplateControlsCache() to bust the cache when:
 * - Reviewer Console mounts (so newly migrated remediation_templates appear
 *   without requiring a hard browser refresh)
 * - Admin tooling updates remediation_templates in-session
 */
const cache = new Map<string, Promise<Set<string>>>();

export function fetchValidTemplateControls(questionnaireKey: string): Promise<Set<string>> {
  const existing = cache.get(questionnaireKey);
  if (existing) return existing;

  const p = (async () => {
    const { data, error } = await (supabase as any)
      .from("remediation_templates")
      .select("control_code")
      .eq("questionnaire_key", questionnaireKey);
    if (error) {
      console.warn("[validTemplateControls] fetch failed", error);
      // Do NOT persist a failed/empty result — let the next call retry.
      cache.delete(questionnaireKey);
      return new Set<string>();
    }
    return new Set<string>((data ?? []).map((r: any) => r.control_code as string));
  })();

  cache.set(questionnaireKey, p);
  return p;
}

/**
 * Clear the in-memory cache so the next fetchValidTemplateControls() call
 * re-queries the database. Pass a questionnaireKey to clear a single entry,
 * or omit to clear all entries.
 */
export function clearValidTemplateControlsCache(questionnaireKey?: string): void {
  if (questionnaireKey) {
    cache.delete(questionnaireKey);
  } else {
    cache.clear();
  }
}
